import { useHealthStore } from '@/stores/health';
import {
  Activity,
  BrainCircuit,
  Database,
  BarChart3,
  FileText,
  Users,
  Layers,
  Wand2,
  Zap,
  Package,
  ListChecks,
  Paintbrush,
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Search,
  Bot,
  FlaskConical,
  Tags,
  TrendingUp,
  ArrowUpRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={clsx(
        'inline-block w-2 h-2 rounded-full flex-shrink-0',
        ok ? 'bg-emerald-500' : 'bg-red-400',
      )}
    />
  );
}

function ComponentRow({
  label,
  active,
  detail,
}: {
  label: string;
  active: boolean;
  detail?: string;
}) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5">
        <StatusDot ok={active} />
        <span className="text-sm text-slate-700">{label}</span>
      </div>
      <span
        className={clsx(
          'text-xs font-medium',
          active ? 'text-emerald-600' : 'text-slate-400',
        )}
      >
        {detail ?? (active ? 'Active' : 'Inactive')}
      </span>
    </div>
  );
}

export function DashboardPage() {
  const { ready, health, loading, error } = useHealthStore();
  const c = ready?.components;

  const modeLabel: Record<string, string> = {
    full: 'Full Mode',
    insights_and_reports: 'Insights + Reports',
    graphiti: 'Graphiti Only',
    reports_only: 'Reports Only',
    degraded: 'Degraded',
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">
            Service Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time monitoring of agent orchestration, creative production,
            and cognitive subsystems.
          </p>
        </div>
        {health && (
          <div className="text-right">
            <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              Version
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs font-bold text-navy-900">
                {health.version}
              </span>
              {ready && (
                <span className="bg-navy-900 text-brand-teal text-[9px] font-mono px-2 py-0.5 rounded uppercase">
                  {ready.mode}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Service Status"
          value={health?.status === 'healthy' ? 'Healthy' : 'Unreachable'}
          accent={health?.status === 'healthy'}
        />
        <StatCard
          icon={Layers}
          label="Operating Mode"
          value={ready ? (modeLabel[ready.mode] ?? ready.mode) : '—'}
          accent={ready?.ready}
        />
        <StatCard
          icon={Users}
          label="Clients Loaded"
          value={ready?.data?.clients_loaded?.toString() ?? '—'}
        />
        <StatCard
          icon={Database}
          label="Personas Loaded"
          value={ready?.data?.personas_loaded?.toString() ?? '—'}
        />
      </div>

      {/* Main grid: Component status + quick actions + capabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Component status */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-navy-900 text-sm">
              Component Status
            </h2>
            {loading && (
              <span className="text-[10px] text-slate-400 font-mono">
                refreshing...
              </span>
            )}
          </div>
          <div className="px-5 py-2">
            {c ? (
              <>
                <ComponentRow
                  label="Graph Provider"
                  active={!!c.graph_provider}
                  detail={c.graph_provider ?? 'Not configured'}
                />
                <ComponentRow
                  label="Graphiti Engine"
                  active={c.graphiti_engine}
                />
                <ComponentRow
                  label="Context Generator"
                  active={c.context_generator}
                />
                <ComponentRow
                  label="Insights Agent"
                  active={c.insights_agent}
                />
                <ComponentRow
                  label="Cube.dev"
                  active={c.cube_configured}
                />
                <ComponentRow
                  label="Reports Agent"
                  active={c.reports_agent}
                />
                <ComponentRow
                  label="Persona Service"
                  active={c.persona_service}
                  detail={
                    c.persona_sources
                      ? `NG: ${c.persona_sources.neurograph ? 'yes' : 'no'} | CG: ${c.persona_sources.cognitive_graph ? 'yes' : 'no'}`
                      : undefined
                  }
                />
                <ComponentRow
                  label="Data Loaded"
                  active={c.data_loaded}
                  detail={
                    ready?.data?.topics_loaded != null
                      ? `${ready.data.topics_loaded} topics`
                      : undefined
                  }
                />
              </>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                {loading ? 'Loading...' : 'No data available'}
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-semibold text-navy-900 text-sm mb-4">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <QuickAction
                to="/agent-lab"
                icon={FlaskConical}
                label="Agent Lab"
                description="MCP-style brief & persona flows against Core API"
              />
              <QuickAction
                to="/research"
                icon={Search}
                label="Start Research"
                description="Run customer or market research"
              />
              <QuickAction
                to="/ad-generation"
                icon={Zap}
                label="Ad Generation"
                description="Track ad pack generation pipelines"
              />
              <QuickAction
                to="/brand-design"
                icon={Paintbrush}
                label="Brand Design"
                description="Extract brand tokens from storefronts"
              />
              <QuickAction
                to="/graph"
                icon={BrainCircuit}
                label="Explore Graph"
                description="Browse cognitive knowledge graph"
              />
              <QuickAction
                to="/insights"
                icon={BarChart3}
                label="Query Insights"
                description="Natural language analytics"
              />
            </div>
          </div>

          {/* API docs link */}
          <a
            href="/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-navy-900 text-brand-teal rounded-lg text-xs font-bold hover:bg-navy-800 transition-all shadow-glow hover:shadow-glow-strong"
          >
            <ExternalLink size={14} />
            API Documentation
          </a>
        </div>
      </div>

      {/* Capabilities Overview */}
      <div>
        <h2 className="font-semibold text-navy-900 text-sm mb-4">
          Platform Capabilities
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <CapabilityCard
            to="/research"
            icon={Search}
            title="Customer Research"
            description="Planning, web research, enrichment, and resolution pipeline for customer profiles."
            color="bg-blue-50 text-blue-600 border-blue-100"
          />
          <CapabilityCard
            to="/generation"
            icon={Wand2}
            title="Generation Workflows"
            description="Multi-step creative generation with briefs, playbooks, and asset production."
            color="bg-purple-50 text-purple-600 border-purple-100"
          />
          <CapabilityCard
            to="/ad-generation"
            icon={Zap}
            title="Ad Pack Pipeline"
            description="End-to-end ad generation across personas with automated review cycles."
            color="bg-amber-50 text-amber-600 border-amber-100"
          />
          <CapabilityCard
            to="/assets"
            icon={Package}
            title="Asset Pipeline"
            description="Brand design tokens, generated assets, and creative output management."
            color="bg-emerald-50 text-emerald-600 border-emerald-100"
          />
          <CapabilityCard
            to="/graph"
            icon={BrainCircuit}
            title="Cognitive Graph"
            description="FalkorDB-backed knowledge graph with semantic search and entity relationships."
            color="bg-indigo-50 text-indigo-600 border-indigo-100"
          />
          <CapabilityCard
            to="/taxonomy"
            icon={Tags}
            title="Ad Taxonomy"
            description="Form & meaning taxonomy dimensions, creative formulas, and reference ads."
            color="bg-teal-50 text-teal-600 border-teal-100"
          />
          <CapabilityCard
            to="/personas"
            icon={Users}
            title="Personas"
            description="Buyer personas with cognitive graph enrichment, KPIs, and context factors."
            color="bg-rose-50 text-rose-600 border-rose-100"
          />
          <CapabilityCard
            to="/brand-design"
            icon={Paintbrush}
            title="Brand Design"
            description="Automated storefront extraction, style analysis, and LLM token synthesis."
            color="bg-violet-50 text-violet-600 border-violet-100"
          />
          <CapabilityCard
            to="/skills"
            icon={Sparkles}
            title="Creative Skills"
            description="Frameworks, script templates, video platforms, and generation pipelines."
            color="bg-orange-50 text-orange-600 border-orange-100"
          />
          <CapabilityCard
            to="/queue"
            icon={ListChecks}
            title="Queue Control"
            description="Task queue monitoring, budget utilization, NVI scoring, and dispatch."
            color="bg-slate-50 text-slate-600 border-slate-200"
          />
        </div>
      </div>

      {/* Agent Orchestration Summary */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-semibold text-navy-900 text-sm mb-4 flex items-center gap-2">
          <Bot size={16} className="text-brand-teal" />
          Agent Orchestration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AgentCard
            title="Research Agent"
            description="Plan → Research → Answer workflow via LangGraph"
            to="/agents"
            status="active"
          />
          <AgentCard
            title="Customer Research Agent"
            description="Planning → WebResearch → Enrichment → Resolution → Finalization"
            to="/research"
            status="active"
          />
          <AgentCard
            title="Market Research Agent"
            description="Multi-agent: Query, Concept, Scenario, Taxonomy, ProductEnrichment"
            to="/research"
            status="active"
          />
          <AgentCard
            title="Brand Design Agent"
            description="Extraction → Analysis → Synthesis → Persistence via LangGraph"
            to="/brand-design"
            status="active"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider">
          {label}
        </span>
        <Icon size={16} className="text-slate-300" />
      </div>
      <p
        className={clsx(
          'text-lg font-bold',
          accent === true
            ? 'text-emerald-600'
            : accent === false
              ? 'text-red-500'
              : 'text-navy-900',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function QuickAction({
  to,
  icon: Icon,
  label,
  description,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all group"
    >
      <div className="w-9 h-9 rounded-lg bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0 group-hover:bg-brand-teal/20 transition-colors">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-navy-900">{label}</p>
        <p className="text-xs text-slate-500 truncate">{description}</p>
      </div>
    </Link>
  );
}

function CapabilityCard({
  to,
  icon: Icon,
  title,
  description,
  color,
}: {
  to: string;
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-soft hover:border-brand-teal/30 transition-all group"
    >
      <div className="flex items-start justify-between mb-2">
        <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center border', color)}>
          <Icon size={14} />
        </div>
        <ArrowUpRight size={12} className="text-slate-300 group-hover:text-brand-teal transition-colors" />
      </div>
      <h3 className="text-sm font-bold text-navy-900 mb-1">{title}</h3>
      <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2">
        {description}
      </p>
    </Link>
  );
}

function AgentCard({
  title,
  description,
  to,
  status,
}: {
  title: string;
  description: string;
  to: string;
  status: string;
}) {
  return (
    <Link
      to={to}
      className="bg-slate-50 rounded-lg border border-slate-100 p-4 hover:bg-slate-100 hover:border-slate-200 transition-all group"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
        <span className="text-[9px] uppercase font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-100">
          {status}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        {description}
      </p>
    </Link>
  );
}
